import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, Trash2, DoorOpen, Search, ChevronRight, ChevronLeft, Check, LogOut, Clock, ToggleLeft, ToggleRight, Pencil, Grid3X3, AlertTriangle, PawPrint } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { getBoardingRooms, createBoardingRoom, updateBoardingRoom, deleteBoardingRoom, createBoardingCage, deleteBoardingCage, updateBoardingCage, getBoardingRecords, boardingCheckIn, boardingCheckOut, deleteBoardingRecord, getCustomers, getBoardingLogs } from '../services/api';
import BottomNav from '../components/BottomNav';
import PetAvatar from '../components/PetAvatar';
import { toast } from 'sonner';

const formatDT = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('tr-TR') + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
};

const calcCost = (checkIn, checkOut, dailyRate) => {
  const diff = new Date(checkOut) - new Date(checkIn);
  const hours = Math.max(diff / 3600000, 0);
  const cost = (dailyRate / 24) * hours;
  return { hours: Math.round(hours * 100) / 100, cost: Math.round(cost * 100) / 100 };
};

const formatCurrency = (v) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(v) + ' TL';

const Boarding = () => {
  const [rooms, setRooms] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [roomName, setRoomName] = useState('');
  const [editingRoom, setEditingRoom] = useState(null);
  const [addCageFor, setAddCageFor] = useState(null);
  const [cageName, setCageName] = useState('');
  const [editingCage, setEditingCage] = useState(null);
  const [checkInModal, setCheckInModal] = useState(null);
  const [checkOutModal, setCheckOutModal] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [locationHistory, setLocationHistory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [showEmptyOnly, setShowEmptyOnly] = useState(false);

  const load = useCallback(async () => {
    try {
      const [roomsRes, recordsRes] = await Promise.all([getBoardingRooms(), getBoardingRecords('active')]);
      setRooms(roomsRes.data);
      setRecords(recordsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredRecords = searchQuery.trim()
    ? records.filter(r => {
        const q = searchQuery.toLowerCase();
        return (r.pet_name || '').toLowerCase().includes(q) || (r.customer_name || '').toLowerCase().includes(q) || (r.customer_phone || '').includes(q) || (r.pet_microchip_no || '').toLowerCase().includes(q) || (r.customer_tc || '').includes(q);
      })
    : records;

  const getRecordsForLocation = (roomId, cageId) => {
    return filteredRecords.filter(r => r.room_id === roomId && (cageId ? r.cage_id === cageId : !r.cage_id));
  };

  const handleAddRoom = async () => {
    if (!roomName.trim()) return;
    try {
      await createBoardingRoom({ name: roomName.trim() });
      setRoomName(''); setShowAddRoom(false);
      toast.success('Oda oluşturuldu');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  const handleEditRoom = async (roomId) => {
    if (!roomName.trim()) return;
    try {
      await updateBoardingRoom(roomId, { name: roomName.trim() });
      setRoomName(''); setEditingRoom(null);
      toast.success('Oda güncellendi');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  const handleDeleteRoom = async (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    setConfirmDialog({
      title: 'Oda Sil',
      message: `"${room?.name || ''}" odasını silmek istediğinize emin misiniz?`,
      onConfirm: async () => {
        try {
          await deleteBoardingRoom(roomId);
          toast.success('Oda silindi');
          load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        setConfirmDialog(null);
      }
    });
  };

  const handleAddCage = async (roomId) => {
    if (!cageName.trim()) return;
    try {
      await createBoardingCage({ name: cageName.trim(), room_id: roomId });
      setCageName(''); setAddCageFor(null);
      toast.success('Kafes oluşturuldu');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  const handleDeleteCage = async (cageId) => {
    const cage = rooms.flatMap(r => r.cages || []).find(c => c.id === cageId);
    setConfirmDialog({
      title: 'Kafes Sil',
      message: `"${cage?.name || ''}" kafesini silmek istediğinize emin misiniz?`,
      onConfirm: async () => {
        try {
          await deleteBoardingCage(cageId);
          toast.success('Kafes silindi');
          load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        setConfirmDialog(null);
      }
    });
  };

  const handleEditCage = async (cageId) => {
    if (!cageName.trim()) return;
    try {
      await updateBoardingCage(cageId, { name: cageName.trim() });
      setCageName(''); setEditingCage(null);
      toast.success('Kafes güncellendi');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  const activeCount = records.length;

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-24 lg:pb-4" data-testid="boarding-page">
      <header className="bg-white px-4 pt-12 lg:pt-4 pb-4 shadow-sm sticky top-0 z-10">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Konaklama</h1>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowHistory(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#6b6de3]/10 text-[#6b6de3]"
              data-testid="boarding-history-btn">
              <Clock size={14} /> Geçmiş
            </button>
            <button onClick={() => { setShowAddRoom(true); setRoomName(''); }}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-[#6b6de3] rounded-lg hover:bg-[#5658c4]"
              data-testid="add-room-btn">
              <DoorOpen size={14} /> Oda Yönetimi
            </button>
          </div>
        </div>
        {activeCount > 0 && (
          <div className="flex items-center gap-2 bg-green-50 rounded-xl px-3 py-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs font-medium text-green-700">{activeCount} aktif konaklama</span>
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={16} />
          <Input type="search" placeholder="Hasta veya Müşteri Ara (İsim, telefon, çip no)" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 pr-9 h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl" data-testid="boarding-search-input" />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8E8E93]"><X size={14} /></button>
          )}
        </div>
        {/* Empty Rooms Toggle */}
        <div className="flex items-center justify-between mt-3" data-testid="empty-rooms-filter">
          <span className="text-sm text-[#8E8E93] flex items-center gap-1.5">
            <DoorOpen size={14} className="text-[#6b6de3]" />
            Boş odalar
          </span>
          <button onClick={() => setShowEmptyOnly(!showEmptyOnly)} data-testid="empty-rooms-toggle">
            {showEmptyOnly
              ? <ToggleRight size={36} className="text-[#6b6de3]" />
              : <ToggleLeft size={36} className="text-[#C7C7CC]" />}
          </button>
        </div>
      </header>

      <main className="p-4">
        {loading ? (
          <div className="py-12 flex justify-center">
            <div className="w-8 h-8 border-3 border-[#6b6de3] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : rooms.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm text-center py-12">
            <DoorOpen size={48} className="text-[#C7C7CC] mx-auto mb-3" />
            <p className="text-[#8E8E93]">Henüz oda eklenmemiş</p>
            <Button onClick={() => { setShowAddRoom(true); setRoomName(''); }} className="mt-4 bg-[#6b6de3] hover:bg-[#5658c4] rounded-full">
              <Plus size={18} className="mr-1" /> İlk Odayı Ekle
            </Button>
          </div>
        ) : (() => {
          const displayRooms = showEmptyOnly
            ? rooms.filter(room => {
                const directRecs = filteredRecords.filter(r => r.room_id === room.id && !r.cage_id);
                const cageRecs = (room.cages || []).reduce((sum, c) => sum + filteredRecords.filter(r => r.cage_id === c.id).length, 0);
                return directRecs.length === 0 && cageRecs === 0;
              })
            : rooms;
          return displayRooms.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm text-center py-12">
              <DoorOpen size={48} className="text-[#C7C7CC] mx-auto mb-3" />
              <p className="text-[#8E8E93]">Boş oda bulunamadı</p>
            </div>
          ) : (
            <div className={`grid gap-4 ${displayRooms.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
              {displayRooms.map(room => (
                <RoomCard
                  key={room.id}
                  room={room}
                  records={records}
                  getRecordsForLocation={getRecordsForLocation}
                  onAddCage={(roomId) => { setAddCageFor(roomId); setCageName(''); setEditingCage(null); }}
                  onCheckIn={(roomId, cageId) => setCheckInModal({ roomId, cageId })}
                  onCheckOut={(record) => setCheckOutModal(record)}
                  onDeleteRecord={async (id) => {
                    const rec = records.find(r => r.id === id);
                    setConfirmDialog({
                      title: 'Kayıt Sil',
                      message: `${rec?.pet_name || ''} - ${rec?.customer_name || ''} konaklama kaydını silmek istediğinize emin misiniz?`,
                      onConfirm: async () => {
                        await deleteBoardingRecord(id);
                        toast.success('Kayıt silindi');
                        load();
                        setConfirmDialog(null);
                      }
                    });
                  }}
                  onShowHistory={(roomId, cageId, label) => setLocationHistory({ roomId, cageId, label })}
                />
              ))}
            </div>
          );
        })()}
      </main>

      <BottomNav />

      {/* Room Management Modal */}
      {showAddRoom && (
        <ModalOverlay onClose={() => setShowAddRoom(false)}>
          <div className="p-5 space-y-4">
            <h3 className="text-lg font-bold">Odalar</h3>
            <div className="flex gap-2">
              <Input value={roomName} onChange={e => setRoomName(e.target.value)} placeholder="Oda adı" autoFocus className="h-11 bg-[#F2F2F7] border-0 rounded-xl flex-1" data-testid="room-name-input" />
              <Button onClick={() => { if (editingRoom) { handleEditRoom(editingRoom); } else { handleAddRoom(); } }} disabled={!roomName.trim()} className="h-11 bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl px-5" data-testid="save-room-btn">
                {editingRoom ? 'Kaydet' : 'Ekle'}
              </Button>
            </div>
            {rooms.length > 0 && (
              <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
                {rooms.map(r => (
                  <div key={r.id} className="flex items-center justify-between bg-[#F2F2F7] rounded-xl px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <DoorOpen size={14} className="text-[#6b6de3]" />
                      <span className="text-sm font-medium text-gray-900">{r.name}</span>
                      <span className="text-[10px] text-[#8E8E93]">{(r.cages || []).length} kafes</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => { setEditingRoom(r.id); setRoomName(r.name); }} className="p-1.5 rounded-lg hover:bg-white text-[#8E8E93]" data-testid={`edit-room-${r.id}`}>
                        <Pencil size={13} />
                      </button>
                      <button onClick={async () => { await handleDeleteRoom(r.id); }} className="p-1.5 rounded-lg hover:bg-red-50 text-[#FF3B30]" data-testid={`delete-room-${r.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ModalOverlay>
      )}

      {/* Cage Management Modal */}
      {addCageFor && (
        <ModalOverlay onClose={() => { setAddCageFor(null); setEditingCage(null); }}>
          <div className="p-5 space-y-4">
            <h3 className="text-lg font-bold">{rooms.find(r => r.id === addCageFor)?.name} - Kafesler</h3>
            <div className="flex gap-2">
              <Input value={cageName} onChange={e => setCageName(e.target.value)} placeholder="Kafes adı" autoFocus className="h-11 bg-[#F2F2F7] border-0 rounded-xl flex-1" data-testid="cage-name-input" />
              <Button onClick={() => { if (editingCage) { handleEditCage(editingCage); } else { handleAddCage(addCageFor); } }} disabled={!cageName.trim()} className="h-11 bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl px-5" data-testid="save-cage-btn">
                {editingCage ? 'Kaydet' : 'Ekle'}
              </Button>
            </div>
            {(() => {
              const cages = rooms.find(r => r.id === addCageFor)?.cages || [];
              return cages.length > 0 ? (
                <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
                  {cages.map(c => (
                    <div key={c.id} className="flex items-center justify-between bg-[#F2F2F7] rounded-xl px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <Grid3X3 size={13} className="text-[#8E8E93]" />
                        <span className="text-sm font-medium text-gray-900">{c.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setEditingCage(c.id); setCageName(c.name); }} className="p-1.5 rounded-lg hover:bg-white text-[#8E8E93]">
                          <Pencil size={13} />
                        </button>
                        <button onClick={async () => { await handleDeleteCage(c.id); }} className="p-1.5 rounded-lg hover:bg-red-50 text-[#FF3B30]" data-testid={`delete-cage-${c.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null;
            })()}
          </div>
        </ModalOverlay>
      )}

      {/* Check-In Modal */}
      {checkInModal && (
        <CheckInModal
          roomId={checkInModal.roomId}
          cageId={checkInModal.cageId}
          onClose={() => setCheckInModal(null)}
          onSuccess={() => { setCheckInModal(null); load(); }}
        />
      )}

      {/* Check-Out Modal */}
      {checkOutModal && (
        <CheckOutModal
          record={checkOutModal}
          onClose={() => setCheckOutModal(null)}
          onSuccess={() => { setCheckOutModal(null); load(); }}
        />
      )}

      {/* Location History Modal */}
      {locationHistory && (
        <LocationHistoryModal
          roomId={locationHistory.roomId}
          cageId={locationHistory.cageId}
          label={locationHistory.label}
          onClose={() => setLocationHistory(null)}
        />
      )}

      {/* Confirm Dialog */}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      {/* Global History Modal */}
      {showHistory && (
        <BoardingHistoryModal onClose={() => setShowHistory(false)} />
      )}
    </div>
  );
};


// ─── Room Card ─────────────────────────────────────────
function RoomCard({ room, getRecordsForLocation, onAddCage, onCheckIn, onCheckOut, onDeleteRecord, onShowHistory }) {
  const directRecords = getRecordsForLocation(room.id, null);
  const cages = room.cages || [];

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid={`room-${room.id}`}>
      {/* Room Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#6b6de3]/5 to-transparent border-b border-gray-100">
        <div className="flex items-center gap-2">
          <DoorOpen size={18} className="text-[#6b6de3]" />
          <h3 className="font-bold text-gray-900">{room.name}</h3>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onCheckIn(room.id, null)} className="p-1.5 rounded-lg hover:bg-[#6b6de3]/10 text-[#6b6de3]" title="Hasta Ekle" data-testid={`room-checkin-${room.id}`}>
            <span className="relative inline-flex items-center"><PawPrint size={16} /><Plus size={9} className="absolute -top-0.5 -right-1 stroke-[3]" /></span>
          </button>
          <button onClick={() => onAddCage(room.id)} className="p-1.5 rounded-lg hover:bg-gray-100 text-[#8E8E93]" title="Kafesler" data-testid={`room-add-cage-${room.id}`}>
            <Grid3X3 size={16} />
          </button>
          <button onClick={() => onShowHistory(room.id, null, room.name)} className="p-1.5 rounded-lg hover:bg-gray-100 text-[#8E8E93]" title="Oda Geçmişi" data-testid={`room-history-${room.id}`}>
            <Clock size={14} />
          </button>
        </div>
      </div>

      <div className="p-3 space-y-2">
        {/* Direct room records (no cage) */}
        {directRecords.map(rec => (
          <RecordCard key={rec.id} record={rec} onCheckOut={onCheckOut} onDelete={onDeleteRecord} />
        ))}

        {/* Cages */}
        {cages.map(cage => {
          const cageRecords = getRecordsForLocation(room.id, cage.id);
          return (
            <div key={cage.id} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`cage-${cage.id}`}>
              <div className="flex items-center justify-between px-3 py-2 bg-[#F2F2F7]">
                <div className="flex items-center gap-1.5">
                  <Grid3X3 size={13} className="text-[#8E8E93]" />
                  <span className="text-xs font-semibold text-gray-700">{cage.name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => onCheckIn(room.id, cage.id)} className="p-1 rounded hover:bg-white text-[#6b6de3]" title="Hasta Ekle" data-testid={`cage-checkin-${cage.id}`}>
                    <span className="relative inline-flex items-center"><PawPrint size={14} /><Plus size={8} className="absolute -top-0.5 -right-1 stroke-[3]" /></span>
                  </button>
                  <button onClick={() => onShowHistory(room.id, cage.id, `${room.name} / ${cage.name}`)} className="p-1 rounded hover:bg-white text-[#8E8E93]" title="Kafes Geçmişi">
                    <Clock size={12} />
                  </button>
                </div>
              </div>
              {cageRecords.length > 0 && (
                <div className="p-2 space-y-1.5">
                  {cageRecords.map(rec => (
                    <RecordCard key={rec.id} record={rec} onCheckOut={onCheckOut} onDelete={onDeleteRecord} compact />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {directRecords.length === 0 && cages.length === 0 && (
          <p className="text-xs text-[#8E8E93] text-center py-4">Boş oda</p>
        )}
      </div>
    </div>
  );
}


// ─── Record Card ───────────────────────────────────────
function RecordCard({ record, onCheckOut, onDelete, compact }) {
  const elapsed = () => {
    const diff = Date.now() - new Date(record.check_in).getTime();
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    if (h > 24) return `${Math.floor(h / 24)} gün ${h % 24} saat`;
    if (h === 0) return `${m} dk`;
    return `${h} saat ${m} dk`;
  };

  return (
    <div className={`flex items-center gap-2 bg-green-50 rounded-xl ${compact ? 'px-2 py-1.5' : 'px-3 py-2'}`} data-testid={`boarding-record-${record.id}`}>
      <PetAvatar species={record.pet_species} gender={record.pet_gender} size={compact ? 12 : 14} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`font-semibold text-gray-900 ${compact ? 'text-[11px]' : 'text-xs'}`}>{record.pet_name}</span>
          {record.pet_breed && <span className="text-[10px] text-[#8E8E93]">{record.pet_breed}</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] text-[#6b6de3] font-medium">{record.customer_name}</span>
          {record.customer_phone && <a href={`tel:${record.customer_phone}`} className="text-[10px] text-[#8E8E93] hover:underline">{record.customer_phone}</a>}
          <span className="text-[10px] text-[#8E8E93]">{elapsed()}</span>
          {!record.is_free && <span className="text-[10px] text-[#FF9500] font-medium">{record.daily_rate} TL/gün</span>}
          {record.is_free && <span className="text-[10px] text-green-600 font-medium">Ücretsiz</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={() => onCheckOut(record)} className="p-1.5 rounded-lg bg-[#FF9500]/10 hover:bg-[#FF9500]/20 text-[#FF9500]" title="Çıkış" data-testid={`checkout-${record.id}`}>
          <LogOut size={14} />
        </button>
        <button onClick={() => onDelete(record.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-[#FF3B30]" title="Sil">
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}


// ─── Check-In Modal ────────────────────────────────────
function CheckInModal({ roomId, cageId, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedPet, setSelectedPet] = useState(null);
  const [dailyRate, setDailyRate] = useState('');
  const [isFree, setIsFree] = useState(false);
  const [checkInDate, setCheckInDate] = useState(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  });
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await getCustomers(query); setResults(r.data); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const handleSubmit = async () => {
    if (!selectedPet || !selectedCustomer) return;
    if (!isFree && (!dailyRate || parseFloat(dailyRate) <= 0)) {
      toast.error('Günlük ücret girin veya ücretsiz seçin'); return;
    }
    setSaving(true);
    try {
      await boardingCheckIn({
        room_id: roomId, cage_id: cageId || null,
        pet_id: selectedPet.id, customer_id: selectedCustomer.id,
        daily_rate: isFree ? 0 : parseFloat(dailyRate),
        is_free: isFree, check_in: checkInDate,
        notes: notes || null
      });
      toast.success('Konaklama kaydı oluşturuldu');
      onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="checkin-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white sm:rounded-2xl w-full sm:max-w-md lg:max-w-3xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-bold">
            {step === 1 ? 'Müşteri Seç' : step === 2 ? 'Hasta Seç' : 'Konaklama Bilgileri'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100"><X size={20} className="text-gray-400" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {step === 1 && (
            <>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" />
                <Input type="search" placeholder="Müşteri Ara (İsim, telefon veya TCKN)" value={query} onChange={e => setQuery(e.target.value)}
                  className="h-11 pl-9 text-sm bg-[#F2F2F7] border-0 rounded-xl" autoFocus data-testid="boarding-customer-search" />
              </div>
              {results.map(c => (
                <button key={c.id} onClick={() => { setSelectedCustomer(c); setStep(2); }}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-[#F2F2F7] rounded-xl hover:bg-gray-200 text-left" data-testid={`boarding-cust-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{c.name}</p>
                    <p className="text-xs text-[#8E8E93]">{c.phone}</p>
                    {c.pets?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {c.pets.map(p => (
                          <span key={p.id} className="inline-flex items-center gap-0.5 text-[10px] bg-white px-1.5 py-0.5 rounded-md text-gray-600">
                            <PetAvatar species={p.species} gender={p.gender} size={10} /> {p.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ChevronRight size={18} className="text-[#C7C7CC] shrink-0 ml-2" />
                </button>
              ))}
            </>
          )}

          {step === 2 && selectedCustomer && (
            <>
              <p className="text-sm text-[#8E8E93]">{selectedCustomer.name} - Hasta seçin:</p>
              {(selectedCustomer.pets || []).length > 0 ? selectedCustomer.pets.map(pet => (
                <button key={pet.id} onClick={() => { setSelectedPet(pet); setStep(3); }}
                  className="w-full flex items-center gap-3 px-3 py-3 bg-[#F2F2F7] rounded-xl hover:bg-gray-200 text-left" data-testid={`boarding-pet-${pet.id}`}>
                  <PetAvatar species={pet.species} gender={pet.gender} size={20} />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{pet.name}</p>
                    <p className="text-xs text-[#8E8E93]">{pet.breed || pet.species}</p>
                  </div>
                </button>
              )) : (
                <p className="text-sm text-[#8E8E93] text-center py-6">Bu müşterinin hastası yok</p>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <div className="bg-[#F2F2F7] rounded-xl px-3 py-2.5 flex items-center gap-2">
                <PetAvatar species={selectedPet?.species} gender={selectedPet?.gender} size={16} />
                <div>
                  <p className="text-sm font-medium text-gray-900">{selectedPet?.name}</p>
                  <p className="text-xs text-[#8E8E93]">{selectedCustomer?.name}</p>
                </div>
              </div>

              <div>
                <Label className="text-xs text-[#8E8E93]">Giriş Tarihi ve Saati</Label>
                <Input type="datetime-local" value={checkInDate} onChange={e => setCheckInDate(e.target.value)}
                  className="h-11 bg-[#F2F2F7] border-0 rounded-xl text-sm mt-1" data-testid="checkin-datetime" />
              </div>

              <div className="flex items-center justify-between bg-[#F2F2F7] rounded-xl px-3 py-2.5">
                <span className="text-sm text-gray-700">Ücretsiz Konaklama</span>
                <button onClick={() => setIsFree(!isFree)} data-testid="free-toggle">
                  {isFree ? <ToggleRight size={32} className="text-[#34C759]" /> : <ToggleLeft size={32} className="text-[#C7C7CC]" />}
                </button>
              </div>

              {!isFree && (
                <div>
                  <Label className="text-xs text-[#8E8E93]">Günlük Ücret (TL)</Label>
                  <Input type="number" value={dailyRate} onChange={e => setDailyRate(e.target.value)} placeholder="500"
                    className="h-11 bg-[#F2F2F7] border-0 rounded-xl text-sm mt-1" data-testid="daily-rate-input" />
                </div>
              )}

              <div>
                <Label className="text-xs text-[#8E8E93]">Notlar</Label>
                <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Opsiyonel"
                  className="h-10 bg-[#F2F2F7] border-0 rounded-xl text-sm mt-1" />
              </div>

              <Button onClick={handleSubmit} disabled={saving} className="w-full h-11 bg-[#34C759] hover:bg-green-600 rounded-xl text-sm font-semibold" data-testid="checkin-submit">
                <Check size={16} className="mr-1.5" /> Giriş Kaydet
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Check-Out Modal ───────────────────────────────────
function CheckOutModal({ record, onClose, onSuccess }) {
  const [checkOutDate, setCheckOutDate] = useState(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  });
  const [saving, setSaving] = useState(false);

  const preview = checkOutDate ? calcCost(record.check_in, checkOutDate, record.daily_rate) : null;

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const res = await boardingCheckOut(record.id, { check_out: checkOutDate });
      toast.success(`Çıkış yapıldı - ${formatCurrency(res.data.total_cost)}`);
      onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex items-center justify-center p-4 sm:inset-0" data-testid="checkout-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white sm:rounded-2xl shadow-2xl w-full sm:max-w-md lg:max-w-3xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] overflow-hidden p-5 space-y-4">
        <h3 className="text-lg font-bold">Çıkış İşlemi</h3>

        <div className="bg-[#F2F2F7] rounded-xl px-3 py-2.5 flex items-center gap-2">
          <PetAvatar species={record.pet_species} gender={record.pet_gender} size={16} />
          <div>
            <p className="text-sm font-medium text-gray-900">{record.pet_name}</p>
            <p className="text-xs text-[#8E8E93]">{record.customer_name}</p>
          </div>
        </div>

        <div className="text-xs text-[#8E8E93] space-y-1">
          <p>Giriş: <span className="font-medium text-gray-900">{formatDT(record.check_in)}</span></p>
          <p>Günlük Ücret: <span className="font-medium text-gray-900">{record.is_free ? 'Ücretsiz' : `${record.daily_rate} TL`}</span></p>
        </div>

        <div>
          <Label className="text-xs text-[#8E8E93]">Çıkış Tarihi ve Saati</Label>
          <Input type="datetime-local" value={checkOutDate} onChange={e => setCheckOutDate(e.target.value)}
            className="h-11 bg-[#F2F2F7] border-0 rounded-xl text-sm mt-1" data-testid="checkout-datetime" />
        </div>

        {preview && !record.is_free && (
          <div className="bg-[#FF9500]/10 rounded-xl p-3 space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-[#8E8E93]">Toplam Süre</span>
              <span className="font-semibold text-gray-900">{preview.hours >= 24 ? `${Math.floor(preview.hours / 24)} gün ${Math.round(preview.hours % 24)} saat` : `${Math.round(preview.hours * 10) / 10} saat`}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-[#8E8E93]">Hesaplama</span>
              <span className="text-gray-600">{record.daily_rate}/24 x {Math.round(preview.hours * 100) / 100} saat</span>
            </div>
            <div className="flex justify-between text-sm font-bold border-t border-[#FF9500]/20 pt-1.5 mt-1.5">
              <span className="text-[#FF9500]">Toplam Ücret</span>
              <span className="text-[#FF9500]">{formatCurrency(preview.cost)}</span>
            </div>
          </div>
        )}

        {record.is_free && (
          <div className="bg-green-50 rounded-xl p-3 text-center">
            <span className="text-sm font-semibold text-green-700">Ücretsiz Konaklama</span>
          </div>
        )}

        <Button onClick={handleSubmit} disabled={saving} className="w-full h-11 bg-[#FF9500] hover:bg-orange-600 rounded-xl text-sm font-semibold" data-testid="checkout-submit">
          <LogOut size={16} className="mr-1.5" /> Çıkış Yap
        </Button>
      </div>
    </div>
  );
}


// ─── Boarding History Modal (unified) ──────────────────
function getWeekRange(offset = 0) {
  const now = new Date();
  const day = now.getDay();
  const mon = new Date(now);
  mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
  mon.setHours(0, 0, 0, 0);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  sun.setHours(23, 59, 59, 999);
  return { start: mon, end: sun };
}

function formatWeekLabel(offset) {
  if (offset === 0) return 'Bu Hafta';
  if (offset === -1) return 'Geçen Hafta';
  const { start, end } = getWeekRange(offset);
  const fmt = (d) => d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
  return `${fmt(start)} - ${fmt(end)}`;
}

function BoardingHistoryModal({ onClose }) {
  const [records, setRecords] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [weekOffset, setWeekOffset] = useState(0);

  useEffect(() => {
    Promise.all([
      getBoardingRecords('completed').then(r => setRecords(r.data)).catch(() => {}),
      getBoardingLogs(200).then(r => setLogs(r.data)).catch(() => {})
    ]).finally(() => setLoading(false));
  }, []);

  const actionLabels = {
    room_created: 'Oda Eklendi', room_updated: 'Oda Güncellendi', room_deleted: 'Oda Silindi',
    cage_created: 'Kafes Eklendi', cage_updated: 'Kafes Güncellendi', cage_deleted: 'Kafes Silindi',
    check_in: 'Giriş Yapıldı', check_out: 'Çıkış Yapıldı', record_deleted: 'Kayıt Silindi',
  };

  const actionColors = {
    room_created: 'bg-blue-50 text-blue-700', room_updated: 'bg-blue-50 text-blue-600', room_deleted: 'bg-red-50 text-red-700',
    cage_created: 'bg-indigo-50 text-indigo-700', cage_updated: 'bg-indigo-50 text-indigo-600', cage_deleted: 'bg-red-50 text-red-600',
    check_in: 'bg-green-50 text-green-700', check_out: 'bg-orange-50 text-orange-700', record_deleted: 'bg-red-50 text-red-700',
  };

  const { start: weekStart, end: weekEnd } = getWeekRange(weekOffset);

  // Merge records and logs into one chronological list
  const allItems = [
    ...records.map(r => ({ type: 'record', data: r, date: r.check_out || r.check_in || r.created_at })),
    ...logs.map(l => ({ type: 'log', data: l, date: l.created_at }))
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Filter by week
  const weekFiltered = allItems.filter(item => {
    const d = new Date(item.date);
    return d >= weekStart && d <= weekEnd;
  });

  const filtered = searchQuery.trim()
    ? weekFiltered.filter(item => {
        const q = searchQuery.toLowerCase();
        if (item.type === 'record') {
          const r = item.data;
          return (r.pet_name || '').toLowerCase().includes(q) || (r.customer_name || '').toLowerCase().includes(q) || (r.customer_phone || '').includes(q) || (r.pet_microchip_no || '').toLowerCase().includes(q) || (r.customer_tc || '').includes(q);
        }
        const l = item.data;
        return (l.target_name || '').toLowerCase().includes(q) || (l.user_name || '').toLowerCase().includes(q) || (l.details?.customer_name || '').toLowerCase().includes(q);
      })
    : weekFiltered;

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="boarding-history-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white sm:rounded-2xl w-full sm:max-w-md lg:max-w-3xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Geçmiş</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100"><X size={20} className="text-gray-400" /></button>
        </div>

        {/* Week Selector */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-[#F2F2F7]/50">
          <button onClick={() => setWeekOffset(w => w - 1)} className="p-1.5 rounded-lg hover:bg-white text-[#8E8E93]" data-testid="week-prev">
            <ChevronLeft size={18} />
          </button>
          <button onClick={() => setWeekOffset(0)} className={`text-sm font-semibold px-3 py-1 rounded-lg transition-colors ${weekOffset === 0 ? 'text-[#6b6de3] bg-[#6b6de3]/10' : 'text-gray-700 hover:bg-white'}`} data-testid="week-label">
            {formatWeekLabel(weekOffset)}
          </button>
          <button onClick={() => setWeekOffset(w => w + 1)} disabled={weekOffset >= 0} className="p-1.5 rounded-lg hover:bg-white text-[#8E8E93] disabled:opacity-30" data-testid="week-next">
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="py-8 flex justify-center"><div className="w-6 h-6 border-2 border-[#6b6de3] border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <>
              {allItems.length > 0 && (
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={14} />
                  <Input type="search" placeholder="Ara..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                    className="pl-8 h-9 text-sm bg-[#F2F2F7] border-0 rounded-xl" />
                </div>
              )}
              {filtered.length > 0 ? (
                <div className="space-y-2">
                  {filtered.map(item => {
                    const isRecord = item.type === 'record';
                    const d = item.data;
                    const petName = isRecord ? d.pet_name : d.target_name;
                    const petSpecies = isRecord ? d.pet_species : d.details?.pet_species;
                    const petGender = isRecord ? d.pet_gender : d.details?.pet_gender;
                    const customerName = isRecord ? d.customer_name : d.details?.customer_name;
                    const customerPhone = isRecord ? d.customer_phone : d.details?.customer_phone;
                    const userName = isRecord ? null : d.user_name;
                    const actionLabel = !isRecord ? actionLabels[d.action] : null;
                    const actionColor = !isRecord ? (actionColors[d.action] || 'bg-gray-100 text-gray-600') : null;
                    const cost = isRecord ? (d.is_free ? null : d.total_cost) : d.details?.total_cost;
                    const isFree = isRecord ? d.is_free : false;
                    const totalHours = isRecord ? d.total_hours : d.details?.total_hours;

                    return (
                      <div key={`${item.type}-${d.id}`} className="bg-[#F2F2F7] rounded-xl px-3 py-2.5 flex items-center gap-2" data-testid={`history-item-${d.id}`}>
                        <PetAvatar species={petSpecies} gender={petGender} size={14} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {actionLabel && (
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${actionColor}`}>{actionLabel}</span>
                            )}
                            <span className="text-xs font-semibold text-gray-900">{petName}</span>
                          </div>
                          {customerName && <p className="text-[10px] text-[#6b6de3] font-medium">{customerName}</p>}
                          {isRecord && (
                            <div className="flex items-center gap-1.5 text-[10px] text-[#8E8E93]">
                              <span>{formatDT(d.check_in)}</span>
                              <span>→</span>
                              <span>{formatDT(d.check_out)}</span>
                            </div>
                          )}
                          {!isRecord && <p className="text-[10px] text-[#8E8E93]">{formatDT(d.created_at)}</p>}
                          {customerPhone && <p className="text-[10px] text-[#8E8E93]">{customerPhone}</p>}
                          {d.details?.old_name && <p className="text-[10px] text-[#8E8E93]">Eski ad: {d.details.old_name}</p>}
                          {userName && <p className="text-[10px] text-[#C7C7CC]">{userName}</p>}
                        </div>
                        <div className="text-right shrink-0">
                          {isFree && <span className="text-[10px] font-semibold text-green-600">Ücretsiz</span>}
                          {cost != null && !isFree && <span className="text-[10px] font-bold text-[#FF9500]">{formatCurrency(cost)}</span>}
                          {totalHours != null && <p className="text-[10px] text-[#8E8E93]">{totalHours >= 24 ? `${Math.floor(totalHours / 24)} gün ${Math.round(totalHours % 24)} saat` : totalHours < 1 ? `${Math.round(totalHours * 60)} dk` : `${Math.round(totalHours)} saat`}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock size={36} className="text-[#C7C7CC] mx-auto mb-2" />
                  <p className="text-sm text-[#8E8E93]">Geçmiş kayıt yok</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Location History Modal ────────────────────────────
function LocationHistoryModal({ roomId, cageId, label, onClose }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBoardingRecords('completed').then(r => {
      const filtered = r.data.filter(rec => rec.room_id === roomId && (cageId ? rec.cage_id === cageId : true));
      setRecords(filtered);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [roomId, cageId]);

  const formatHours = (h) => {
    if (!h) return '-';
    if (h >= 24) return `${Math.floor(h / 24)} gün ${Math.round(h % 24)} saat`;
    if (h < 1) return `${Math.round(h * 60)} dk`;
    return `${Math.round(h)} saat`;
  };

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="location-history-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white sm:rounded-2xl w-full sm:max-w-md lg:max-w-3xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Konaklama Geçmişi</h2>
            <p className="text-xs text-[#8E8E93]">{label}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100"><X size={20} className="text-gray-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="py-8 flex justify-center"><div className="w-6 h-6 border-2 border-[#6b6de3] border-t-transparent rounded-full animate-spin" /></div>
          ) : records.length > 0 ? (
            <div className="space-y-2">
              {records.map(r => (
                <div key={r.id} className="bg-[#F2F2F7] rounded-xl px-3 py-2.5 flex items-center gap-2">
                  <PetAvatar species={r.pet_species} gender={r.pet_gender} size={14} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-gray-900">{r.pet_name}</span>
                      <span className="text-[10px] text-[#6b6de3] font-medium">{r.customer_name}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-[#8E8E93]">
                      <span>{formatDT(r.check_in)}</span>
                      <span>→</span>
                      <span>{formatDT(r.check_out)}</span>
                    </div>
                    {r.customer_phone && <p className="text-[10px] text-[#8E8E93]">{r.customer_phone}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    {r.is_free ? (
                      <span className="text-[10px] font-semibold text-green-600">Ücretsiz</span>
                    ) : (
                      <span className="text-[10px] font-bold text-[#FF9500]">{formatCurrency(r.total_cost || 0)}</span>
                    )}
                    <p className="text-[10px] text-[#8E8E93]">{formatHours(r.total_hours)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock size={36} className="text-[#C7C7CC] mx-auto mb-2" />
              <p className="text-sm text-[#8E8E93]">Bu konumda geçmiş kayıt yok</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Confirm Dialog ────────────────────────────────────
function ConfirmDialog({ title, message, onConfirm, onCancel }) {
  const [loading, setLoading] = useState(false);
  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9999] flex items-center justify-center p-4 sm:inset-0" data-testid="confirm-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white sm:rounded-2xl shadow-2xl w-full sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[#FF3B30]/10 flex items-center justify-center shrink-0">
            <AlertTriangle size={18} className="text-[#FF3B30]" />
          </div>
          <h3 className="text-base font-bold text-gray-900">{title}</h3>
        </div>
        <p className="text-sm text-[#8E8E93]">{message}</p>
        <div className="flex gap-2">
          <Button onClick={onCancel} variant="outline" className="flex-1 h-10 rounded-xl text-sm">
            Vazgec
          </Button>
          <Button
            onClick={async () => { setLoading(true); await onConfirm(); setLoading(false); }}
            disabled={loading}
            className="flex-1 h-10 bg-[#FF3B30] hover:bg-red-600 rounded-xl text-sm font-semibold"
            data-testid="confirm-delete-btn"
          >
            {loading ? 'Siliniyor...' : 'Sil'}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ─── Modal Overlay ─────────────────────────────────────
function ModalOverlay({ children, onClose }) {
  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex items-center justify-center p-4 sm:inset-0">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white sm:rounded-2xl shadow-2xl w-full sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden">{children}</div>
    </div>
  );
}


export default Boarding;
